import api from '../utils/api';
import { isMobileContainer, isNativePlatform } from '../utils/device';
import { resolveNotificationLink } from '../utils/notificationLinks';
import { markNotificationRead } from '../utils/notifications';
import {
  bindNotificationLaunchListener,
  consumeNotificationLaunchData,
} from '../utils/notificationLaunch';

let currentToken = null;
let listenersBound = false;
let launchListenerBound = false;
let navigateRef = null;
let userRef = null;
let onErrorRef = null;
let pendingNotification = null;

function extractNotificationData(notification) {
  return notification?.data || notification?.notification?.data || notification?.extra || {};
}

function isPublicReceiptLink(link) {
  let raw = String(link || '').trim();
  if (raw.startsWith('/#/')) raw = raw.slice(2);
  const path = raw.split('?')[0];
  return path === '/invoice-portal' || path.startsWith('/invoice-portal');
}

function buildNotificationPayload(notification) {
  const data = extractNotificationData(notification);
  return {
    // Prefer alert_title: branded FCM title is often the school name.
    title: data.alert_title || data.title || notification?.title,
    alert_title: data.alert_title,
    school_name: data.school_name,
    school_logo_url: data.school_logo_url,
    link: data.link,
    payment_id: data.payment_id,
    inquiry_id: data.inquiry_id,
    entity_id: data.entity_id,
    notification_id: data.notification_id,
    chat_user_id: data.chat_user_id,
    conversation_id: data.conversation_id,
    ticket_id: data.ticket_id,
  };
}

function navigateToTarget(target) {
  if (!target || !navigateRef) return;
  const qIdx = target.indexOf('?');
  if (qIdx === -1) {
    navigateRef(target);
    return;
  }
  navigateRef({
    pathname: target.slice(0, qIdx),
    search: target.slice(qIdx),
  });
}

function resolveTarget(notification) {
  const payload = buildNotificationPayload(notification);
  const rawLink = String(payload.link || '').trim();

  if (isPublicReceiptLink(rawLink)) {
    return resolveNotificationLink(userRef || {}, payload, '/invoice-portal');
  }

  if (!userRef) return null;
  return resolveNotificationLink(userRef, payload);
}

async function markReadIfNeeded(notification) {
  const data = extractNotificationData(notification);
  const id = data.notification_id;
  if (!id) return;
  try {
    await markNotificationRead(id);
  } catch {
    // Mark-read failure should not block navigation
  }
}

function flushPendingNotification() {
  if (!pendingNotification) return;
  handleNotificationNavigation(pendingNotification);
}

function handleNotificationNavigation(notification) {
  if (!notification) return;

  const target = resolveTarget(notification);
  const payload = buildNotificationPayload(notification);
  const needsUser = !isPublicReceiptLink(String(payload.link || '').trim());

  if (!navigateRef || (needsUser && !userRef)) {
    pendingNotification = notification;
    return;
  }

  pendingNotification = null;
  markReadIfNeeded(notification);
  navigateToTarget(target);
}

async function ensureAndroidChannel() {
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.createChannel({
      id: 'school_erp_default',
      name: 'School notifications',
      importance: 4,
      visibility: 1,
    });
  } catch {
    // ignore
  }
}

async function registerTokenWithBackend(token) {
  if (!token) return;
  currentToken = token;
  await api.post('/notifications/device-token', {
    token,
    platform: 'android',
    device_label: 'android',
  });
}

export async function unregisterPushToken() {
  if (!currentToken) return;
  try {
    await api.delete('/notifications/device-token', { data: { token: currentToken } });
  } catch {
    // ignore logout cleanup errors
  } finally {
    currentToken = null;
  }
}

function bindLaunchListener() {
  if (launchListenerBound) return;
  launchListenerBound = true;
  bindNotificationLaunchListener((notification) => {
    handleNotificationNavigation(notification);
    window.dispatchEvent(new Event('notificationsRead'));
  });
}

function bindListeners(onError) {
  if (onError) onErrorRef = onError;
  bindLaunchListener();
  if (listenersBound) return;
  listenersBound = true;

  Promise.all([
    import('@capacitor/push-notifications'),
    import('@capacitor/local-notifications'),
  ]).then(([{ PushNotifications }, { LocalNotifications }]) => {
    PushNotifications.addListener('registration', async (token) => {
      try {
        await registerTokenWithBackend(token.value);
      } catch (e) {
        console.warn('Failed to register push token', e);
        onErrorRef?.('Could not register this device for push notifications. Check your connection and try again.');
      }
    });

    PushNotifications.addListener('registrationError', (error) => {
      console.warn('Push registration error', error);
      onErrorRef?.('Push registration failed. Rebuild the app with google-services.json and try again.');
    });

    PushNotifications.addListener('pushNotificationReceived', () => {
      // Native SchoolPushMessagingService already shows the tray banner;
      // do not schedule a second LocalNotification here.
      window.dispatchEvent(new Event('notificationsRead'));
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      handleNotificationNavigation(action?.notification);
      window.dispatchEvent(new Event('notificationsRead'));
    });

    LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
      handleNotificationNavigation(action?.notification);
      window.dispatchEvent(new Event('notificationsRead'));
    });
  }).catch((e) => console.warn('Push listeners unavailable', e));
}

export async function initPushNotifications({ navigate, user, onError } = {}) {
  if (!isMobileContainer() || !isNativePlatform()) return;
  navigateRef = navigate || navigateRef;
  userRef = user || userRef;
  if (onError) onErrorRef = onError;

  bindListeners(onError);
  flushPendingNotification();
  await ensureAndroidChannel();

  try {
    const launchNotification = await consumeNotificationLaunchData();
    if (launchNotification) {
      handleNotificationNavigation(launchNotification);
    }
  } catch {
    // ignore cold-start launch read errors
  }

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const { LocalNotifications } = await import('@capacitor/local-notifications');

    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') {
      onError?.('Notification permission was denied. Enable notifications in device settings.');
      return;
    }

    await LocalNotifications.requestPermissions();
    await PushNotifications.register();
  } catch (e) {
    const message = e?.message || 'Push notification setup failed';
    console.warn('Push init failed', e);
    onError?.(message);
  }
}

export function updatePushNotificationContext({ navigate, user, onError } = {}) {
  if (navigate) navigateRef = navigate;
  if (user) userRef = user;
  if (onError) onErrorRef = onError;
  if (isMobileContainer() && isNativePlatform()) {
    bindListeners(onError);
    flushPendingNotification();
  }
}
